import axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

// ==========================================
// 401-Behandlung (Session ungueltig / abgelaufen)
// ==========================================

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Registriert die Reaktion auf eine 401-Antwort (z. B. Cache leeren + zum Login).
 * Wird von einer React-Komponente innerhalb des Routers gesetzt, da hier
 * weder Router noch QueryClient verfuegbar sind.
 */
export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null) => {
  unauthorizedHandler = handler;
};

/**
 * Oeffentliche Auth-Endpoints, bei denen 401 eine fachliche Antwort ist
 * (z. B. falsches Passwort beim Login) und KEINE abgelaufene Session.
 */
const PUBLIC_AUTH_ENDPOINTS = [
  "/api/auth/login",
  "/api/auth/register",
  "/api/auth/verify-email",
  "/api/auth/logout",
];

const isPublicAuthEndpoint = (url?: string) =>
  !!url && PUBLIC_AUTH_ENDPOINTS.some((endpoint) => url.startsWith(endpoint));

/**
 * Liefert den HTTP-Status eines Fehlers aus dem apiClient (oder undefined bei
 * Netzwerkfehlern / Nicht-Axios-Fehlern).
 */
export const getHttpStatus = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

// 1. Zentrale Konfiguration
export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// 2. Response Interceptor (Globales Error Handling & Daten auspacken)
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response.data;
  },
  (error: AxiosError) => {
    // Globales Error Logging im Development Modus
    if (import.meta.env.MODE === "development") {
      console.error("[API Error]", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
      });
    }

    // Nicht (mehr) eingeloggt -> globale Reaktion (Logout + Weiterleitung).
    if (
      error.response?.status === 401 &&
      !isPublicAuthEndpoint(error.config?.url)
    ) {
      unauthorizedHandler?.();
    }

    return Promise.reject(error);
  },
);

// 3. Das vereinfachte Interface für deine App
export const apiClient = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.get<T, T>(url, config),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.post<T, T>(url, data, config),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.put<T, T>(url, data, config),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    axiosInstance.patch<T, T>(url, data, config),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    axiosInstance.delete<T, T>(url, config),
};
